import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../stores/appStore';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import { gatewayRef } from '../utils/gatewayRef';
import { formatTokenCount } from '../utils/tokenFormatting';

function secondsRemaining(expiresAt: number, now: number): number {
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
}

function isLongConversationReason(reason: string): boolean {
  const normalized = reason.trim().toLowerCase();
  return normalized.includes('long') || normalized.includes('conversation') || normalized.includes('token') || normalized.includes('context');
}

export default function ResetConfirmDialog() {
  const request = useAppStore((state) => state.resetConfirmQueue[0] ?? null);
  const removeResetConfirm = useAppStore((state) => state.removeResetConfirm);
  const sessionContextFill = useAppStore((state) => state.sessionContextFill);
  const [now, setNow] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState<'confirm' | 'deny' | null>(null);

  useEffect(() => {
    if (!request) {
      setSubmitting(null);
      return;
    }

    setNow(Date.now());
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 100);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [request]);

  useEffect(() => {
    if (!request) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      void sendDecision('deny');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [request]);

  const countdown = useMemo(() => {
    if (!request) {
      return 0;
    }
    return secondsRemaining(request.expiresAt, now);
  }, [now, request]);

  const contextFill = request ? sessionContextFill[request.sessionKey] : undefined;
  const isLongConversationWarning = Boolean(request && isLongConversationReason(request.reason));

  const sendDecision = async (decision: 'confirm' | 'deny') => {
    if (!request || submitting) {
      return;
    }

    const currentRequest = request;
    const closeDialog = () => {
      removeResetConfirm(currentRequest.nonce);
    };
    const client = gatewayRef.current;

    if (!client?.connected) {
      closeDialog();
      if (decision === 'confirm') {
        window.alert('Connection lost. Trying to reconnect...');
      }
      return;
    }

    setSubmitting(decision);
    try {
      await client.sendResetDecision(decision, currentRequest.nonce);
      closeDialog();
    } catch (error) {
      console.warn('[ResetConfirmDialog] Failed to send reset decision:', error);
      closeDialog();
      if (decision === 'confirm') {
        window.alert(toFriendlyErrorMessage(error, 'chat-send'));
      }
    }
  };

  if (!request) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          void sendDecision('deny');
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-confirm-title"
        className="w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-2xl"
      >
        <h2
          id="reset-confirm-title"
          className="text-xl font-semibold text-foreground"
          style={{ fontFamily: 'var(--app-heading-font)' }}
        >
          {isLongConversationWarning ? 'Long conversation warning' : 'Reset session?'}
        </h2>
        {isLongConversationWarning ? (
          <>
            <p className="mt-3 text-sm text-foreground/90">
              Your conversation is at {formatTokenCount(contextFill)} tokens.
            </p>
            <p className="mt-2 text-sm text-foreground/90">
              Starting a fresh session will keep replies fast and reduce context pressure.
            </p>
          </>
        ) : (
          <p className="mt-3 text-sm text-foreground/90">
            The current conversation will be archived and a new session will start.
          </p>
        )}
        <p className="mt-3 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
          Auto-cancels in {countdown}s
        </p>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => {
              void sendDecision('deny');
            }}
            disabled={submitting !== null}
            className="rounded-lg border border-input bg-secondary px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            Keep session
          </button>
          <button
            type="button"
            onClick={() => {
              void sendDecision('confirm');
            }}
            disabled={submitting !== null}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            style={{ backgroundColor: '#ef4444' }}
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
